import { Logger } from 'loglevel';
import { EdiAdminBaseService } from './EdiAdminBaseService';
import {
  oauthClientProfileSchema,
  oauthClientProfileListSchema,
  oauthClientRoleListSchema,
  type OAuthClientProfile,
  type OAuthClientProfileRole,
  type OAuthClientRole,
} from '../types/schemas/clientProfile.schema';

export type { OAuthClientProfile, OAuthClientProfileRole, OAuthClientRole };

export class EdiAdminClientProfilesService extends EdiAdminBaseService {
  constructor(
    private readonly getAccessToken: () => Promise<string>,
    logger: Logger,
  ) {
    super(logger);
  }
  async fetchGetClientProfiles(): Promise<OAuthClientProfile[]> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientProfile/GetClientProfiles`;

    this.logger.info('Fetching client profiles:', {
      url: apiFetchUrl,
    });

    const response = await fetch(apiFetchUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        'Erreur lors de la récupération des profils client',
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(
      oauthClientProfileListSchema,
      Array.isArray(data) ? data : [],
      'GetClientProfiles',
    );
  }

  async fetchGetClientRoles(): Promise<OAuthClientRole[]> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientProfile/GetClientRoles`;

    this.logger.info('Fetching client roles:', {
      url: apiFetchUrl,
    });

    const response = await fetch(apiFetchUrl, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        'Erreur lors de la récupération des roles client',
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(
      oauthClientRoleListSchema,
      Array.isArray(data) ? data : [],
      'GetClientRoles',
    );
  }

  async fetchUpdateClientDefaultProfile(profileInstanceId: string): Promise<OAuthClientProfile> {
    const accessToken = await this.getAccessToken();
    const apiFetchUrl = `${this.apiBaseUrl}/OAuthClient/ClientProfile/UpdateClientDefaultProfile`;

    this.logger.info('Updating client default profile:', {
      url: apiFetchUrl,
      profileInstanceId,
    });

    const response = await fetch(apiFetchUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(profileInstanceId),
    });

    this.logger.info('📥 Response fetch:', {
      url: response.url,
      status: response.status,
      headers: Object.fromEntries(response.headers.entries()),
    });

    if (!response.ok) {
      await this.handleEdiApiServiceResponseError(
        response,
        'Erreur lors de la modification du profil par défaut du client',
      );
    }

    await this.checkEdiApiServiceResponseContentType(response);

    const data: unknown = await response.json();

    return this.parseResponse(oauthClientProfileSchema, data, 'UpdateClientDefaultProfile');
  }
}
